/**
 * 文档：https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js
 *
 */

import type { IAppleClientParams, IGoogleClientParams, IOauth, IUploadFile } from '../types';
import { parseJwt } from './helper';

const loadAppleSDK = async () => {
  return new Promise((resolve, reject) => {
    const scriptElement = document.createElement('script');
    scriptElement.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    scriptElement.onload = resolve;
    scriptElement.onerror = reject;
    document.body.append(scriptElement);
  });
};

const loadCloudKitSDK = async (cloudkitJsSrc: string) => {
  return new Promise((resolve, reject) => {
    const scriptElement = document.createElement('script');
    scriptElement.src = cloudkitJsSrc || 'https://cdn.apple-cloudkit.com/ck/2/cloudkit.js';
    scriptElement.onload = resolve;
    scriptElement.onerror = reject;
    document.body.append(scriptElement);
  });
};

export interface IClientInit {
  clientId: string;
  scope: string;
}

export class AppleOauth implements IOauth {
  static __instance: AppleOauth;
  accessToken?: string;
  expires_time: number | undefined;
  container?: any;
  initialized: boolean = false;
  constructor() {
    if (AppleOauth.__instance) return AppleOauth.__instance;
    AppleOauth.__instance = this;
  }

  cloudKitSetUpAuth = async () => {
    const res = await this.container.setUpAuth().catch(console.error);
    return {
      isLoginCloud: res,
      redirectURL: this.container?._auth?._signInURL,
    };
  };

  getFileContent = async (fileId: string) => {
    const fileContent = await fetch(fileId).then((response) => response.text());
    return fileContent;
  };

  uploadFile = async (props: IUploadFile) => {
    const record = {
      recordName: props.metadata.name,
      recordType: 'tokenUpType',
      recordChangeTag: props.fileId,
      fields: {
        fileName: {
          value: props.metadata.name,
        },
        fileType: {
          value: props.metadata.mimeType,
        },
        fileData: {
          value: props.file,
        },
      },
    };
    const privateCloudDB = this.container.privateCloudDatabase;
    const response = await privateCloudDB.saveRecords(record);
    if (response.serverErrorCode) {
      throw new Error(response);
    }
    if (response.hasErrors) {
      throw new Error(response.errors[0]);
    } else {
      return response;
    }
  };

  // TOMO: Function to be verified
  deleteFile = async (recordName: string) => {
    const privateCloudDB = this.container.privateCloudDatabase;

    const deleteResponse = await privateCloudDB.deleteRecords(recordName);

    if (deleteResponse.serverErrorCode) {
      throw new Error(deleteResponse.serverErrorCode);
    }

    if (deleteResponse.hasErrors) {
      throw new Error(deleteResponse.errors?.[0]);
    }

    return deleteResponse;
  };

  // TOMO: Function to be verified
  deleteFiles = async (recordNames: string[]) => {
    const privateCloudDB = this.container.privateCloudDatabase;

    const deleteResponse = await privateCloudDB.deleteRecords(recordNames);

    if (deleteResponse.serverErrorCode) {
      throw new Error(deleteResponse.serverErrorCode);
    }

    if (deleteResponse.hasErrors) {
      throw new Error(deleteResponse.errors?.[0]);
    }

    return deleteResponse;
  };

  init = async (clientParams: IAppleClientParams | IGoogleClientParams) => {
    const appleParams = clientParams as IAppleClientParams;
    if (this.initialized) return;
    try {
      await this._initAppleSDK(appleParams);
      await this._initCloudKit(appleParams);
      this.initialized = true;
    } catch (err) {
      console.error(err);
    }
  };

  _initAppleSDK = async (clientParams: IAppleClientParams) => {
    if (!globalThis.AppleID) {
      await loadAppleSDK();
    }
    await globalThis.AppleID.auth.init({
      clientId: clientParams.clientId,
      scope: clientParams.scope,
      redirectURI: clientParams.redirectURI,
      usePopup: clientParams.usePopup,
      state: clientParams.state,
    });
  };

  _initCloudKit = async (clientParams: IAppleClientParams) => {
    if (!globalThis.CloudKit) {
      await loadCloudKitSDK(clientParams.cloudkitJsSrc);
    }
    CloudKit.configure({
      containers: [
        {
          containerIdentifier: clientParams.containerIdentifier,
          apiTokenAuth: {
            apiToken: clientParams.apiToken,
            ckWebAuthToken: clientParams.ckWebAuthToken,
            persist: true,
            signInButton: clientParams.signInButton || {
              id: 'apple-sign-in-button',
              theme: 'medium',
              text: 'Sign in with iCloud',
            },
            signOutButton: clientParams.signOutButton || {
              id: 'apple-sign-out-button',
              theme: 'medium',
            },
          },
          environment: clientParams.environment,
        },
      ],
    });
    this.container = CloudKit.getContainer(clientParams.containerIdentifier);
  };
  authCloudKit = async () => {
    try {
      await this.container.whenUserSignsIn();
    } catch (err) {
      console.error('whenUserSignsIn_error', err);
    }
  };
  signIn = async () => {
    const appleRes = await AppleID.auth.signIn();
    if (appleRes && appleRes.authorization) {
      return appleRes;
    } else {
      return null;
    }
  };
  getUserInfo = async () => {
    const appleRes = await AppleID.auth.signIn();
    if (appleRes && appleRes.authorization) {
      const { id_token } = appleRes.authorization;
      const { email, sub } = parseJwt(id_token);
      return { email, id: sub, picture: '' };
    } else {
      return { email: '', id: '', picture: '' };
    }
  };

  getDriveFiles = async (props: IUploadFile['metadata']) => {
    const privateCloudDB = this.container.privateCloudDatabase;
    const response = await privateCloudDB.fetchRecords(props.name);
    if (response.hasErrors) {
      return {};
    } else {
      return response;
    }
  };

  setAccessToken = () => {
    throw Error('The setAccessToken method is not supported');
  };
}
